<template>
  <div v-if="hasTiers">
    <label class="d-block mb-25">{{ $t('Faixas de taxa da loja') }}</label>
    <p class="text-muted mb-50 small">
      {{
        $t(
          'A taxa cobrada é a faixa correspondente ao total de produtos (descontando grupos isentos).'
        )
      }}
    </p>

    <b-table-simple
      small
      responsive
      class="mb-0 delivery-tiers-preview"
    >
      <b-thead>
        <b-tr>
          <b-th>{{ $t('Faixa (total de produtos)') }}</b-th>
          <b-th class="text-right">
            {{ $t('Taxa') }}
          </b-th>
          <b-th />
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr
          v-for="(tier, index) in orderedTiers"
          :key="index"
          :class="{ 'table-primary': index === activeIndex }"
        >
          <b-td>{{ tierRangeLabel(index) }}</b-td>
          <b-td class="text-right">
            <span v-if="Number(tier.value) === 0">{{ $t('Grátis') }}</span>
            <span v-else>{{ tier.value | currency }}</span>
          </b-td>
          <b-td class="text-right">
            <b-badge
              v-if="index === activeIndex"
              variant="primary"
            >
              {{ $t('Faixa atual') }}
            </b-badge>
          </b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>
  </div>
</template>

<script>
import { BTableSimple, BThead, BTbody, BTr, BTh, BTd, BBadge } from 'bootstrap-vue'

/**
 * Exibição (somente leitura) das faixas de taxa de delivery da loja no card de entrega da venda,
 * destacando a faixa vigente conforme o valor de produtos (base já sem os grupos isentos — D3/D15).
 *
 * A faixa vigente é a de maior "a partir de" menor ou igual ao `baseValue` — mesma regra do
 * cálculo da taxa (`selectDeliveryTierValue`). O limite superior de cada faixa ("até") é derivado
 * do "a partir de" da faixa seguinte, apenas para leitura.
 */
export default {
  components: {
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    BBadge,
  },

  props: {
    // Faixas resolvidas da loja: { productsValueFrom, value }.
    tiers: {
      type: Array,
      default: () => [],
    },
    // Valor de produtos usado para escolher a faixa (já desconta grupos isentos).
    baseValue: {
      type: Number,
      default: 0,
    },
  },

  computed: {
    hasTiers() {
      return Array.isArray(this.tiers) && this.tiers.length > 0
    },

    // Faixas ordenadas por "a partir de" crescente (não muta a prop).
    orderedTiers() {
      return [...this.tiers].sort(
        (a, b) => (a.productsValueFrom ?? 0) - (b.productsValueFrom ?? 0)
      )
    },

    // Índice da faixa vigente: a de maior "a partir de" <= base; nunca abaixo da primeira.
    activeIndex() {
      const value = this.baseValue || 0
      let active = 0
      this.orderedTiers.forEach((tier, index) => {
        if ((tier.productsValueFrom ?? 0) <= value) active = index
      })
      return active
    },
  },

  methods: {
    tierRangeLabel(index) {
      const { currency } = this.$options.filters
      const from = this.orderedTiers[index].productsValueFrom ?? 0
      const next = this.orderedTiers[index + 1]
      if (!next) {
        return `${this.$t('Acima de')} ${currency(from)}`
      }
      const to = (next.productsValueFrom ?? 0) - 0.01
      return `${currency(from)} — ${currency(to)}`
    },
  },
}
</script>

<style lang="scss" scoped>
.delivery-tiers-preview {
  font-size: 0.9rem;
}
</style>
